(() => {
  const identity = (item) =>
    JSON.stringify([
      item.variant_id,
      Object.entries(item.properties || {}).sort(),
      item.selling_plan_allocation?.selling_plan?.id || null
    ]);

  const merchandise = (cart, config) =>
    cart.items.filter(
      (item) =>
        ![config.wrap.id, config.shipment.id].includes(item.variant_id) &&
        !item.properties?._hs_gift_service
    );

  const signature = (items) =>
    JSON.stringify(
      items
        .map((item) => [identity(item), item.quantity])
        .sort()
    );

  const calculate = (groups, items, enabled, wrappingEnabled) => {
    const used = new Map();
    const active = enabled ? groups : [];

    for (const group of active) {
      let count = 0;

      for (const [id, quantity] of Object.entries(group.items)) {
        if (!Number.isInteger(quantity) || quantity < 0) {
          throw new Error('Please enter whole item quantities.');
        }

        if (!quantity) continue;

        const item = items.find((entry) => identity(entry) === id);

        if (!item) {
          throw new Error(
            'An assigned item is no longer in your cart. Review your gifts.'
          );
        }

        used.set(id, (used.get(id) || 0) + quantity);

        if (used.get(id) > item.quantity) {
          throw new Error(
            'More units are assigned to gifts than are in your cart.'
          );
        }

        count += quantity;
      }

      if (!count) {
        throw new Error(
          'Select at least one item for each gift, or remove the empty gift.'
        );
      }
    }

    const personal = items.reduce(
      (sum, item) => sum + item.quantity - (used.get(identity(item)) || 0),
      0
    );

    return {
      personal,
      shipments: Math.max(
        0,
        active.length + (personal > 0 ? 1 : 0) - 1
      ),
      wraps: wrappingEnabled
        ? active.filter((group) => group.wrap).length
        : 0
    };
  };

  const reconcile = (groups, items) => {
    const remaining = new Map(
      items.map((item) => [identity(item), item.quantity])
    );

    let changed = false;

    for (const group of groups) {
      for (const [id, quantity] of Object.entries(group.items)) {
        const next = Math.min(
          Math.max(0, Math.floor(Number(quantity) || 0)),
          remaining.get(id) || 0
        );

        if (next !== quantity) changed = true;

        if (next) {
          group.items[id] = next;
        } else {
          delete group.items[id];
        }

        remaining.set(id, (remaining.get(id) || 0) - next);
      }
    }

    return changed;
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { identity, merchandise, calculate, reconcile };
  }

  if (
    typeof customElements === 'undefined' ||
    customElements.get('gift-options')
  ) {
    return;
  }

  const escape = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        })[char]
    );

  customElements.define(
    'gift-options',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;

        this.config = JSON.parse(
          this.querySelector('[data-gift-config]').textContent
        );

        this.cart = JSON.parse(this.dataset.cart);
        this.items = merchandise(this.cart, this.config);
        this.groups = [];

        try {
          const saved = JSON.parse(
            this.cart.attributes['Gift groups'] || 'null'
          );

          if (saved?.version === 1 && Array.isArray(saved.groups)) {
            this.groups = saved.groups.map((group) => ({
              message: String(group.message || '').slice(0, 200),
              wrap:
                this.config.wrappingEnabled &&
                Boolean(group.wrap),
              items: group.items || {}
            }));
          }
        } catch {
          /* Rebuild malformed saved plans before checkout. */
        }

        this.enabled =
          this.cart.attributes['Gift order'] === 'Yes' ||
          this.groups.length > 0;

        if (
          this.enabled &&
          !this.groups.length &&
          this.items.length
        ) {
          this.groups.push(this.newGroup());

          this.groups[0].items = Object.fromEntries(
            this.items.map((item) => [
              identity(item),
              item.quantity
            ])
          );

          this.groups[0].message = (
            this.cart.attributes['Gift message'] || ''
          ).slice(0, 200);

          this.groups[0].wrap =
            this.config.wrappingEnabled &&
            this.cart.items.some(
              (item) => item.variant_id === this.config.wrap.id
            );
        }

        reconcile(this.groups, this.items);

        this.addEventListener('change', (event) =>
          this.onChange(event)
        );

        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-message]')) {
            this.onChange(event);
          }
        });

        this.addEventListener('click', (event) => {
          if (
            event.target.closest('[data-add-group]') &&
            this.groups.length < this.config.maxGroups
          ) {
            this.groups.push(this.newGroup());
            this.render();
            this.dirty();
            return;
          }

          const remove = event.target.closest('[data-remove-group]');

          if (remove) {
            this.groups.splice(
              Number(remove.dataset.removeGroup),
              1
            );

            this.groups = this.groups.filter((group) =>
              Object.values(group.items).some(
                (quantity) => Number(quantity) > 0
              )
            );

            if (!this.groups.length) {
              this.enabled = false;
            }

            this.render();
            this.querySelector('[data-gift-status]').textContent =
              'Removing gift and updating your cart...';
            this.save(false);
            return;
          }

          if (event.target.closest('[data-save-gifts]')) {
            this.save(false);
          }
        });

        this.submitHandler = (event) => {
          if (
            event.target.id !== 'cart' ||
            (event.submitter && event.submitter.name !== 'checkout')
          ) {
            return;
          }

          event.preventDefault();
          this.save(true);
        };

        document.addEventListener(
          'submit',
          this.submitHandler,
          true
        );

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            ({ cartData }) => {
              if (!cartData?.items || !this.isConnected) {
                return;
              }

              this.cart = cartData;
              this.items = merchandise(cartData, this.config);

              const changed = reconcile(
                this.groups,
                this.items
              );

              if (!this.items.length) {
                this.enabled = false;
              }

              this.render();

              this.dirty(
                changed
                  ? 'Cart quantities changed. Review your gift selections before saving.'
                  : 'Cart updated. Gift charges will be checked when you save or check out.'
              );
            }
          );
        }

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(
          'submit',
          this.submitHandler,
          true
        );

        this.unsubscribe?.();
      }

      newGroup() {
        return {
          message: '',
          wrap: false,
          items: {}
        };
      }

      money(cents) {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en',
          {
            style: 'currency',
            currency: this.config.currency || 'EUR'
          }
        ).format(cents / 100);
      }

      dirty(text = 'Changes are not saved yet.') {
        const saveButton = this.querySelector('[data-save-gifts]');
        const status = this.querySelector('[data-gift-status]');

        if (saveButton) {
          saveButton.hidden = false;
        }

        if (status) {
          status.textContent = text;
        }
      }

      onChange(event) {
        const target = event.target;

        if (target.id === 'Gift-order') {
          this.enabled = target.checked;

          if (this.enabled && !this.groups.length) {
            this.groups.push(this.newGroup());
          }

          this.render();

          if (!this.enabled) {
            this.querySelector('[data-gift-status]').textContent =
              'Removing gift options and updating your cart...';
            this.save(false);
          } else {
            this.dirty();
          }
          return;
        }

        const container = target.closest('[data-group]');

        if (!container) return;

        const group =
          this.groups[Number(container.dataset.group)];

        if (target.matches('[data-message]')) {
          group.message = target.value.slice(0, 200);
        }

        if (target.matches('[data-wrap]')) {
          group.wrap = target.checked;
        }

        if (target.matches('[data-item]')) {
          const item =
            this.items[Number(target.dataset.item)];

          const available = this.availableQuantity(
            item,
            Number(container.dataset.group)
          );

          const requested =
            target.type === 'checkbox'
              ? target.checked
                ? 1
                : 0
              : Number(target.value);

          const quantity = Math.min(
            available,
            Math.max(0, Math.floor(requested || 0))
          );

          group.items[identity(item)] = quantity;

          if (target.type === 'checkbox') {
            target.checked = quantity > 0;
          } else {
            target.value = quantity;
          }

          this.updateAvailability();
        }

        this.summary();
        this.dirty();
      }

      availableQuantity(item, groupIndex) {
        const id = identity(item);

        const assignedElsewhere = this.groups.reduce(
          (sum, group, index) =>
            sum +
            (index === groupIndex
              ? 0
              : group.items[id] || 0),
          0
        );

        return Math.max(
          0,
          item.quantity - assignedElsewhere
        );
      }

      updateAvailability() {
        this.querySelectorAll('[data-item]').forEach((input) => {
          const index = Number(
            input.closest('[data-group]').dataset.group
          );

          const item =
            this.items[Number(input.dataset.item)];

          const available = this.availableQuantity(
            item,
            index
          );

          const row = input.closest('.gift-options__item');

          input.disabled = available === 0;

          if (input.type === 'number') {
            input.max = available;
          }

          row.classList.toggle(
            'gift-options__item--unavailable',
            available === 0
          );

          const availability = row.querySelector(
            '[data-item-availability]'
          );

          if (availability) {
            availability.textContent =
              available === 0
                ? 'Assigned to another gift'
                : `${available} available for this gift`;
          }
        });
      }

      render() {
        const checkbox = this.querySelector('#Gift-order');

        checkbox.checked = this.enabled;
        checkbox.disabled = !this.items.length;

        checkbox.setAttribute(
          'aria-expanded',
          String(this.enabled)
        );

        this.querySelector('#Gift-details').hidden =
          !this.enabled;

        this.querySelector('[data-gift-groups]').innerHTML =
          this.groups
            .map((group, index) => {
              const isLast =
                index === this.groups.length - 1;

              const canAdd =
                this.groups.length < this.config.maxGroups;

              return `
                <section
                  class="gift-options__group"
                  data-group="${index}"
                  role="group"
                  aria-labelledby="Gift-title-${index}"
                >
                  <div class="gift-options__group-header">
                    <span class="gift-options__group-title" id="Gift-title-${index}">
                      Gift ${index + 1}
                    </span>

                    <span class="gift-options__group-actions">
                      ${
                        isLast
                          ? `
                            <button
                              type="button"
                              class="gift-options__add-inline"
                              data-add-group
                              aria-label="Add another gift"
                              title="Add another gift"
                              ${canAdd ? '' : 'disabled'}
                            >
                              <span class="gift-options__add-icon" aria-hidden="true"></span>
                              <span>Add Gift</span>
                            </button>
                          `
                          : ''
                      }

                      <button
                        type="button"
                        class="gift-options__remove"
                        data-remove-group="${index}"
                        aria-label="Remove gift ${index + 1}"
                        title="Remove gift ${index + 1}"
                      >
                        &times;
                      </button>
                    </span>
                  </div>

                  <div class="gift-options__items">
                    ${this.items
                      .map((item, itemIndex) => {
                        const quantity =
                          group.items[identity(item)] || 0;

                        const name = escape(
                          item.product_title || item.title
                        );

                        const variant =
                          item.variant_title &&
                          item.variant_title !== 'Default Title'
                            ? escape(item.variant_title)
                            : '';

                        const image =
                          item.featured_image?.url ||
                          item.image;

                        return `
                          <label
                            class="gift-options__item"
                            for="Gift-item-${index}-${itemIndex}"
                          >
                            ${
                              image
                                ? `<img
                                    src="${escape(image)}"
                                    alt=""
                                    width="48"
                                    height="60"
                                    loading="lazy"
                                  >`
                                : '<span></span>'
                            }

                            <span>
                              ${name}
                              <small>${variant}</small>
                              <small data-item-availability></small>
                            </span>

                            <input
                              id="Gift-item-${index}-${itemIndex}"
                              data-item="${itemIndex}"
                              aria-label="${name}: quantity for gift ${index + 1}"
                              ${
                                item.quantity === 1
                                  ? `type="checkbox" ${
                                      quantity ? 'checked' : ''
                                    }`
                                  : `type="number"
                                     min="0"
                                     max="${item.quantity}"
                                     step="1"
                                     value="${quantity}"`
                              }
                            >
                          </label>
                        `;
                      })
                      .join('')}
                  </div>

                  <label for="Gift-message-${index}">
                    Gift message (optional)
                  </label>

                  <textarea
                    id="Gift-message-${index}"
                    maxlength="200"
                    rows="3"
                    data-message
                    aria-describedby="Gift-limit-${index}"
                  >${escape(group.message)}</textarea>

                  <small id="Gift-limit-${index}">
                    200 characters maximum
                  </small>

                  ${
                    this.config.wrappingEnabled
                      ? `
                        <label class="gift-options__wrapping">
                          <input
                            type="checkbox"
                            data-wrap
                            ${group.wrap ? 'checked' : ''}
                            ${
                              this.config.wrap.available
                                ? ''
                                : 'disabled'
                            }
                          >

                          <span>
                            Add gift wrapping${
                              this.config.wrap.available
                                ? ''
                                : ' (unavailable)'
                            }
                          </span>

                          <span class="gift-options__price">
                            ${this.money(
                              this.config.wrap.price
                            )}
                          </span>
                        </label>
                      `
                      : ''
                  }
                </section>
              `;
            })
            .join('');

        this.updateAvailability();
        this.summary();
      }

      validate() {
        if (
          this.enabled &&
          (!this.groups.length ||
            this.groups.length > this.config.maxGroups)
        ) {
          throw new Error(
            `Please use between 1 and ${this.config.maxGroups} gift groups.`
          );
        }

        const result = calculate(
          this.groups,
          this.items,
          this.enabled,
          this.config.wrappingEnabled
        );

        if (
          result.shipments &&
          (!this.config.shipment.id ||
            !this.config.shipment.available)
        ) {
          throw new Error(
            'Separate gift shipments are currently unavailable. Keep all items in one gift or contact us.'
          );
        }

        if (
          result.wraps &&
          (!this.config.wrap.id ||
            !this.config.wrap.available)
        ) {
          throw new Error(
            'Gift wrapping is currently unavailable. Untick wrapping to continue.'
          );
        }

        if (
          (result.wraps || result.shipments) &&
          this.config.wrap.id === this.config.shipment.id
        ) {
          throw new Error(
            'Gift services are not configured correctly. Please contact us.'
          );
        }

        return result;
      }

      summary() {
        const summary =
          document.querySelector(
            '#main-cart-footer [data-gift-summary]'
          ) ||
          this.querySelector('[data-gift-summary]');

        if (!summary) return;

        try {
          const totals = this.validate();

          summary.textContent =
            `${totals.personal} item(s) for you. ` +
            `Additional shipments: ${totals.shipments} ` +
            `(${this.money(
              totals.shipments * this.config.shipment.price
            )}).` +
            `${
              this.config.wrappingEnabled
                ? ` Wrapping: ${this.money(
                    totals.wraps * this.config.wrap.price
                  )}.`
                : ''
            } ` +
            `Total gift extras: ${this.money(
              totals.shipments * this.config.shipment.price +
                totals.wraps * this.config.wrap.price
            )}.`;
        } catch (error) {
          summary.textContent = error.message;
        }
      }

      async request(path, body) {
        const response = await fetch(
          `${this.dataset.cartUrl}${path}`,
          {
            method: body ? 'POST' : 'GET',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            ...(body
              ? {
                  body: JSON.stringify(body)
                }
              : {}),
            cache: 'no-store'
          }
        );

        const data = await response.json();

        if (
          !response.ok ||
          data.status >= 400 ||
          data.errors
        ) {
          throw new Error(
            data.description ||
              'Your cart could not be updated. Please try again.'
          );
        }

        return data;
      }

      async save(checkout) {
        if (this.busy) return;

        const errorBox = this.querySelector('[role="alert"]');

        errorBox.hidden = true;

        this.busy = true;
        this.setAttribute('aria-busy', 'true');

        const cartItems =
          document.querySelector('#main-cart-items');

        const wasInert = cartItems?.inert;

        if (cartItems) {
          cartItems.inert = true;
        }

        const controls = [
          ...this.querySelectorAll(
            'input, textarea, button'
          ),
          ...document.querySelectorAll('#checkout')
        ];

        const disabled = controls.map(
          (control) => control.disabled
        );

        controls.forEach((control) => {
          control.disabled = true;
        });

        try {
          const quantitiesPending = [
            ...document.querySelectorAll(
              '#cart input[name="updates[]"]'
            )
          ].some(
            (input) =>
              input.value !== input.defaultValue
          );

          if (
            document.querySelector('.cart__items--disabled') ||
            quantitiesPending
          ) {
            throw new Error(
              'Please wait for your cart quantity update to finish, then try again.'
            );
          }

          this.validate();

          const fresh = await this.request('.js');

          const freshItems = merchandise(
            fresh,
            this.config
          );

          const changed =
            signature(this.items) !==
            signature(freshItems);

          this.items = freshItems;

          const adjusted = reconcile(
            this.groups,
            this.items
          );

          if (changed || adjusted) {
            if (!this.items.length) {
              this.enabled = false;
            }

            this.render();
            this.dirty();

            throw new Error(
              'Your cart changed. Review the gift quantities and charges, then save again.'
            );
          }

          const totals = this.validate();

          const updates = {};
          const additions = [];

          for (const [type, quantity] of [
            ['shipment', totals.shipments],
            ['wrap', totals.wraps]
          ]) {
            const variant = this.config[type];

            const lines = fresh.items.filter(
              (item) =>
                item.variant_id === variant.id ||
                item.properties?._hs_gift_service === type
            );

            const current = lines.find(
              (item) =>
                item.variant_id === variant.id
            );

            lines.forEach((item) => {
              updates[item.key] =
                item === current ? quantity : 0;
            });

            if (quantity && !current) {
              additions.push({
                id: variant.id,
                quantity,
                properties: {
                  _hs_gift_service: type
                }
              });
            }
          }

          const active = this.enabled
            ? this.groups.map((group) => ({
                ...group,
                wrap:
                  this.config.wrappingEnabled &&
                  group.wrap
              }))
            : [];

          const attributes = {
            'Gift order': active.length ? 'Yes' : 'No',
            'Gift message': '',
            'Gift groups': active.length
              ? JSON.stringify({
                  version: 1,
                  groups: active
                })
              : '',
            'Additional gift shipments': String(
              totals.shipments
            )
          };

          for (let index = 0; index < 10; index++) {
            const group = active[index];

            attributes[`Gift ${index + 1}`] = group
              ? [
                  ...this.items
                    .filter(
                      (item) =>
                        group.items[identity(item)]
                    )
                    .map(
                      (item) =>
                        `${
                          group.items[identity(item)]
                        } x ${item.title} (variant ${
                          item.variant_id
                        }, SKU ${item.sku || '-'})`
                    ),

                  `Message: ${group.message || '-'}`,

                  `Wrapping: ${
                    group.wrap ? 'Yes' : 'No'
                  }`
                ].join('\n')
              : '';
          }

          const note =
            document.getElementById('Cart-note');

          await this.request('/update.js', {
            attributes,
            updates,
            ...(note
              ? {
                  note: note.value
                }
              : {})
          });

          if (additions.length) {
            await this.request('/add.js', {
              items: additions
            });
          }

          const verified = await this.request('.js');

          for (const [type, expected] of [
            ['shipment', totals.shipments],
            ['wrap', totals.wraps]
          ]) {
            const quantity = verified.items
              .filter(
                (item) =>
                  item.variant_id ===
                    this.config[type].id ||
                  item.properties?._hs_gift_service ===
                    type
              )
              .reduce(
                (sum, item) =>
                  sum + item.quantity,
                0
              );

            if (quantity !== expected) {
              throw new Error(
                'Gift charges could not be confirmed. Please save again before checkout.'
              );
            }
          }

          if (
            signature(this.items) !==
            signature(
              merchandise(verified, this.config)
            )
          ) {
            throw new Error(
              'Your cart changed during saving. Refresh and review your gifts.'
            );
          }

          if (
            (verified.attributes['Gift groups'] || '') !==
              attributes['Gift groups'] ||
            verified.attributes['Gift order'] !==
              attributes['Gift order']
          ) {
            throw new Error(
              'Gift selections changed during saving. Please refresh and review your gifts.'
            );
          }

          if (checkout && this.items.length) {
            window.location.assign(
              this.dataset.checkoutUrl
            );
          } else {
            window.location.reload();
          }
        } catch (error) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
          this.dirty('Changes are not saved yet.');

          errorBox.scrollIntoView({
            block: 'nearest'
          });
        } finally {
          this.busy = false;
          this.removeAttribute('aria-busy');

          controls.forEach((control, index) => {
            control.disabled = disabled[index];
          });

          if (cartItems) {
            cartItems.inert = wasInert;
          }
        }
      }
    }
  );
})();
